import { Box, Drawer, LoadingOverlay } from '@mantine/core'
import { isAxiosError } from 'axios'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  useCreateDeviceSettings,
  useGetDeviceSettings,
  useInvalidateDeviceSettings,
  useUpdateDeviceSettings
} from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'
import { Device, DeviceSettingsPayload } from '@/types/device'
import {
  SubmitFormValues,
  VlmDeviceSettingsForm
} from './VlmDeviceSettingsForm'

type VlmSettingsDrawerProps = {
  opened: boolean
  device: Device
  onClose: () => void
}

export const VlmSettingsDrawer = ({
  opened,
  device,
  onClose
}: VlmSettingsDrawerProps) => {
  const intl = useIntl()
  const { invalidateDeviceSettings } = useInvalidateDeviceSettings(device.id)

  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError
  } = useGetDeviceSettings(device.id, opened)

  const { mutateAsync: createSettings, isPending: isCreating } =
    useCreateDeviceSettings()
  const { mutateAsync: updateSettings, isPending: isUpdating } =
    useUpdateDeviceSettings()

  const isSettingsNotFound =
    isAxiosError(settingsError) && settingsError.response?.status === 404

  const isSettingsError = !!settingsError && !isSettingsNotFound

  const handleSubmit = async ({
    payload: { gpuIndexes, storageLimitGb, retentionDays, modelProfileId },
    isDirty
  }: SubmitFormValues) => {
    const payload: DeviceSettingsPayload = {
      gpu_indexes: gpuIndexes,
      disk_space: storageLimitGb,
      retention_days: retentionDays,
      model_profile_id: modelProfileId
    }

    try {
      if (isSettingsNotFound) {
        await createSettings({ deviceId: device.id, payload })
        await invalidateDeviceSettings()
      } else if (isDirty) {
        await updateSettings({ deviceId: device.id, payload })
        await invalidateDeviceSettings()
      }

      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.hardware.settings.saveError' })
        ),
        'error'
      )
    }
  }

  return (
    <Drawer
      opened={opened}
      title={<FormattedMessage id="vlm.hardware.settings.title" />}
      position="right"
      size="xl"
      onClose={onClose}
    >
      <Box pos="relative" mih={300}>
        <LoadingOverlay
          visible={isLoadingSettings || isCreating || isUpdating}
        />

        <PlatformAlert mb="lg" compact>
          <FormattedMessage id="vlm.hardware.settings.description" />
        </PlatformAlert>

        {!isLoadingSettings &&
          (isSettingsError ? (
            <PlatformAlert variant="error">
              <FormattedMessage id="vlm.hardware.settings.loadError" />
            </PlatformAlert>
          ) : (
            <VlmDeviceSettingsForm
              device={device}
              settings={settings}
              onSubmit={(values) => void handleSubmit(values)}
              onCancel={onClose}
            />
          ))}
      </Box>
    </Drawer>
  )
}
